// compatibility.ts — material family × component matrix.
//
// Read per axis: COMPAT[axis][family][code] = 'D' (default) | 'O' (allowed) | 'X' (forbidden).
// An invalid combination is strictly not selectable, unlike a missing required field which stays a
// forcible warning. An item absent from a family's matrix counts as forbidden. UPZ twins ("U")
// inherit their base ref's family, and the empty option stays compatible with everything.

import { COMPAT, MATERIAL_FAMILY } from "../../data";
import type { Compat, CompatCode } from "../../models/data.model";
import { isProvisionalRef } from "./provisional-material";

/** Input field governed by the compatibility matrix. */
export type CompatField = "material" | "print" | "cut" | "lamination" | "finishing1" | "finishing2";

/** Matrix axis for each field; `material` has no constraint of its own. */
const AXIS: Record<CompatField, keyof Compat | null> = {
  material: null,
  print: "print",
  cut: "cut",
  lamination: "lamination",
  finishing1: "finishing",
  finishing2: "finishing",
};

/**
 * Family of a material; a "U" twin inherits its base ref's family. Null when unknown.
 * @param ref The material reference.
 * @returns The family, or null.
 */
export function materialFamilyOf(ref: string | undefined): string | null {
  if (ref == null) return null;
  if (MATERIAL_FAMILY[ref]) return MATERIAL_FAMILY[ref]!;
  if (ref.endsWith("U") && MATERIAL_FAMILY[ref.slice(0, -1)]) return MATERIAL_FAMILY[ref.slice(0, -1)]!;
  return null;
}

/**
 * Compatibility code of an item for the given material: 'D' | 'O' | 'X'. Returns 'O' when no
 * constraint applies, and 'X' when the family has a matrix for this axis but omits the item.
 * @param field The input field.
 * @param matRef The material reference.
 * @param itemCode The item code to test.
 * @returns The compatibility code.
 */
export function compatCode(field: CompatField, matRef: string | undefined, itemCode: string): CompatCode {
  const fam = materialFamilyOf(matRef);
  if (!fam) return "O";
  const axis = AXIS[field];
  if (!axis) return "O";
  const familyMap = COMPAT[axis][fam];
  if (!familyMap) return "O";
  return familyMap[itemCode] ?? "X";
}

/**
 * Is the item selectable for this material? Everything is visible until a material is chosen, the
 * empty option is always allowed, and otherwise it is forbidden only when the code is 'X'.
 * @param field The input field.
 * @param matRef The material reference.
 * @param itemCode The item code to test.
 * @returns True when the item is selectable.
 */
export function itemAllowed(field: CompatField, matRef: string | undefined, itemCode: string): boolean {
  if (!matRef) return true;
  if (itemCode === "") return true;
  if (isProvisionalRef(itemCode)) return true;
  return compatCode(field, matRef, itemCode) !== "X";
}

/**
 * Filters item codes down to those compatible with the material (used for menus).
 * @param field The input field.
 * @param matRef The material reference.
 * @param codes The candidate item codes.
 * @returns The compatible codes.
 */
export function compatibleOptions(
  field: CompatField,
  matRef: string | undefined,
  codes: readonly string[],
): string[] {
  return codes.filter((c) => itemAllowed(field, matRef, c));
}
